// Copyright (c) 2026 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage } from "react-intl";
import { Button } from "react-bootstrap";

import { IAction, ActionType, raiseAction } from "actions";
import { isSet } from "shared/utils/utils";
import { IState } from "states/states";

import InfoScreen from "view/comp/InfoScreen";
import InfoRow from "view/comp/InfoRow";

import IconUser from "icons/navUser";
import IconCompany from "icons/iconCompany";
import IconRfid from "icons/navRfid";
import IconEmail from "icons/iconEmail";

interface IUserInfoProps {
  userName: string | undefined;
  companyName: string | undefined;
  email: string | undefined;
  rfidNumber: string | undefined;
  userId: number | undefined;
  isRfidUser: boolean;
}

const mapStateToProps = (state: IState) => {
  const curUser = state.site.userList?.find((curItem) => curItem.secId === state.site.user?.secId);

  const props: IUserInfoProps = {
    userName: curUser?.name ?? state.site.user?.realName,
    companyName: state.site.companyList?.find((curItem) => curItem.id === curUser?.companyId)?.name,
    email: curUser?.email ?? state.site.user?.email,
    rfidNumber: state.site.user?.secId,
    userId: curUser?.id,
    isRfidUser: !!state.site.user?.isRfidUser,
  };
  return props;
};

interface IUserInfoDispatch {
  onLogout: () => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IUserInfoDispatch = {
    onLogout: () => {
      dispatch(raiseAction(ActionType.ACT_LOGOUT_REQ));
    },
  };
  return dispatchProps;
};

class UserInfoComponent extends React.Component<IUserInfoProps & IUserInfoDispatch> {
  public render() {
    return (
      <div className="infoScreenPage">
        <InfoScreen
          title={<FormattedMessage id="Current User" />}
          footer={
            <div className="buttonbox">
              <Button
                className="infoScreenButton"
                variant="danger"
                id="logout"
                type="submit"
                onClick={this.props.onLogout}
              >
                <FormattedMessage id="Sign out" />
              </Button>
            </div>
          }
        >
          <p>
            <FormattedMessage id="You are logged in with this user:" />
          </p>
          <table>
            <tbody>
              <InfoRow
                icon={<IconUser />}
                label={<FormattedMessage id="Name" />}
                value={<span title={`Id: ${this.props.userId}`}>{this.props.userName}</span>}
              />
              {isSet(this.props.companyName) && (
                <InfoRow
                  icon={<IconCompany />}
                  label={<FormattedMessage id="Company" />}
                  value={this.props.companyName}
                />
              )}
              {/* RFID users are identified by their card, they have no email address */}
              {this.props.isRfidUser && (
                <InfoRow
                  icon={<IconRfid />}
                  label={<FormattedMessage id="Card Number" />}
                  value={this.props.rfidNumber}
                />
              )}
              {!this.props.isRfidUser && isSet(this.props.email) && (
                <InfoRow
                  icon={<IconEmail />}
                  label={<FormattedMessage id="Email" />}
                  value={this.props.email}
                />
              )}
            </tbody>
          </table>
        </InfoScreen>
      </div>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(UserInfoComponent);
